import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ChevronRight, CheckCircle2, XCircle, RefreshCw, Plus, Wrench, Lock,
} from "lucide-react";
import AuditTrail from "@/components/AuditTrail";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLORS = {
  Pending: "bg-slate-100 text-slate-700 border-slate-200",
  Ongoing: "bg-amber-50 text-amber-800 border-amber-200",
  "Revision Required": "bg-rose-50 text-rose-700 border-rose-200",
  Released: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

function EngBadge({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 rounded text-xs font-semibold uppercase font-mono ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {status}
    </span>
  );
}

export default function EngineeringDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawingDraft, setDrawingDraft] = useState({
    drawing_number: "", general_assembly_drawing_number: "", part_drawing_number: "",
    drawing_description: "", remarks: "",
  });
  const [engineerDraft, setEngineerDraft] = useState("");
  const [revDialog, setRevDialog] = useState(false);
  const [revForm, setRevForm] = useState({ drawing_number: "", revision_number: "R0", drawing_description: "", remarks: "" });
  const [approveDialog, setApproveDialog] = useState(false);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [rejectDialog, setRejectDialog] = useState(false);
  const [reason, setReason] = useState("");

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/engineering/${id}`);
    setD(data);
    const eng = data.engineering || {};
    setDrawingDraft({
      drawing_number: eng.drawing_number || "",
      general_assembly_drawing_number: eng.general_assembly_drawing_number || "",
      part_drawing_number: eng.part_drawing_number || "",
      drawing_description: eng.drawing_description || "",
      remarks: eng.remarks || "",
    });
    setEngineerDraft(eng.design_engineer_id || "");
    // Auto-suggest next revision (R{n+1})
    const nextRev = eng.current_revision
      ? `R${(parseInt(eng.current_revision.replace(/[^\d]/g, "")) || 0) + 1}`
      : "R0";
    setRevForm((v) => ({
      ...v,
      drawing_number: eng.drawing_number || "",
      revision_number: nextRev,
    }));
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  }

  const eng = d.engineering || {};
  const isLocked = eng.status === "Released";
  const canEdit = d.can_edit && !isLocked;
  const missingApprovalFields = [];
  if (!eng.drawing_number) missingApprovalFields.push("Drawing Number");
  if (!eng.current_revision) missingApprovalFields.push("At least one Revision");
  const canApprove = d.can_approve && !isLocked && missingApprovalFields.length === 0;
  const approveDisabledReason = !d.can_approve
    ? "Only Administrator can approve designs."
    : isLocked
      ? "Design is already approved."
      : missingApprovalFields.length
        ? `Missing: ${missingApprovalFields.join(", ")}`
        : null;

  async function saveDrawing() {
    setSaving(true);
    try {
      const payload = { ...drawingDraft };
      if (engineerDraft && engineerDraft !== (eng.design_engineer_id || "")) {
        payload.design_engineer_id = engineerDraft;
      }
      await api.patch(`/engineering/${id}`, payload);
      toast.success("Drawing info updated");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function changeStatus(next) {
    setSaving(true);
    try {
      await api.patch(`/engineering/${id}`, { status: next });
      toast.success(`Status set to ${next}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function submitRevision() {
    if (!revForm.drawing_number.trim() || !revForm.revision_number.trim()) {
      toast.error("Drawing Number and Revision Number are required"); return;
    }
    setSaving(true);
    try {
      await api.post(`/engineering/${id}/revisions`, revForm);
      toast.success(`Revision ${revForm.revision_number} created`);
      setRevDialog(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function approve() {
    setSaving(true);
    try {
      await api.post(`/engineering/${id}/approve`, { remarks: approveRemarks || null });
      toast.success("Design approved successfully.");
      setApproveDialog(false); setApproveRemarks("");
      await refresh();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed to approve design";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  }

  async function reject() {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.post(`/engineering/${id}/reject`, { reason });
      toast.success("Engineering rejected");
      setRejectDialog(false); setReason("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="engineering-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/engineering" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Design Queue
        </Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/cylinders/${d.cylinder.id}`} className="hover:text-slate-900 font-mono">
          #{d.cylinder.cylinder_number}
        </Link>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold flex items-center gap-1.5">
              <Wrench className="w-3 h-3" /> Engineering Release
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">Cylinder #{d.cylinder.cylinder_number}</div>
            <div className="mt-2 text-sm text-slate-600">
              {d.customer_name} · {d.plant_name} · {d.oa_number || "—"}
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <EngBadge status={eng.status || "Pending"} />
            {isLocked && (
              <div className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-3 py-1">
                <Lock className="w-3 h-3" /> Locked · Approved by {eng.approved_by_name}
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2 p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            Drawing Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">General Assembly Drawing #</Label>
              <Input data-testid="eng-ga-drawing-number" value={drawingDraft.general_assembly_drawing_number}
                     onChange={(e) => setDrawingDraft({ ...drawingDraft, general_assembly_drawing_number: e.target.value })}
                     disabled={!canEdit} className="mt-1 font-mono" placeholder="e.g. P1234" />
              <p className="text-[10px] text-slate-500 mt-1">Format example: <span className="font-mono">P1234</span></p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Part Drawing #</Label>
              <Input data-testid="eng-part-drawing-number" value={drawingDraft.part_drawing_number}
                     onChange={(e) => setDrawingDraft({ ...drawingDraft, part_drawing_number: e.target.value })}
                     disabled={!canEdit} className="mt-1 font-mono" placeholder="e.g. 1234" />
              <p className="text-[10px] text-slate-500 mt-1">Format example: <span className="font-mono">1234</span></p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Current Revision</Label>
              <Input value={eng.current_revision || ""} disabled className="mt-1 font-mono" placeholder="—" />
              <p className="text-[10px] text-slate-500 mt-1">Set by creating a new revision below.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Drawing # (legacy / searchable)</Label>
              <Input data-testid="eng-drawing-number" value={drawingDraft.drawing_number}
                     onChange={(e) => setDrawingDraft({ ...drawingDraft, drawing_number: e.target.value })}
                     disabled={!canEdit} className="mt-1 font-mono" placeholder="Optional — auto-syncs with GA #" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Description (optional)</Label>
              <Input data-testid="eng-drawing-desc" value={drawingDraft.drawing_description}
                     onChange={(e) => setDrawingDraft({ ...drawingDraft, drawing_description: e.target.value })}
                     disabled={!canEdit} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Design Engineer</Label>
              <Select value={engineerDraft || "none"} onValueChange={(v) => setEngineerDraft(v === "none" ? "" : v)} disabled={!canEdit}>
                <SelectTrigger data-testid="eng-engineer-select" className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {(d.engineers || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks</Label>
              <Textarea data-testid="eng-remarks" rows={2} value={drawingDraft.remarks}
                        onChange={(e) => setDrawingDraft({ ...drawingDraft, remarks: e.target.value })}
                        disabled={!canEdit} className="mt-1" />
            </div>
          </div>
          {canEdit && (
            <div className="mt-4 flex flex-wrap justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {eng.status === "Pending" && (
                  <Button size="sm" variant="outline" data-testid="eng-set-ongoing" onClick={() => changeStatus("Ongoing")}>
                    Start Working (Ongoing)
                  </Button>
                )}
                <Button size="sm" data-testid="new-revision-btn" variant="outline" onClick={() => setRevDialog(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> New Revision
                </Button>
                <Button size="sm" variant="outline" data-testid="request-rev-btn"
                        onClick={() => { setRejectDialog(true); setReason(""); }}
                        className="border-rose-200 text-rose-700 hover:bg-rose-50">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Request Revision
                </Button>
              </div>
              <Button size="sm" data-testid="eng-save" onClick={saveDrawing} disabled={saving}
                      className="bg-blue-900 hover:bg-blue-800 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          )}
          {d.can_approve && !isLocked && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
              <div className="text-xs text-slate-500" data-testid="eng-approve-hint">
                {approveDisabledReason ? (
                  <span className="text-amber-700">⚠ {approveDisabledReason}</span>
                ) : (
                  <span className="text-emerald-700">All mandatory fields complete — ready for approval.</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" data-testid="eng-reject-btn" variant="outline"
                        onClick={() => setRejectDialog(true)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
                <Button size="sm" data-testid="eng-approve-btn"
                        onClick={() => {
                          if (!canApprove) {
                            toast.error(approveDisabledReason || "Cannot approve at this time");
                            return;
                          }
                          setApproveDialog(true);
                        }}
                        disabled={saving}
                        title={approveDisabledReason || "Approve & Release engineering"}
                        className={canApprove
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-slate-300 hover:bg-slate-400 text-slate-700 cursor-not-allowed"}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve Design
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card className="border-slate-200 p-5">
            <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Lead Time</div>
            <div className="space-y-2 text-sm">
              <KV label="Assigned Date" value={eng.assigned_date ? eng.assigned_date.slice(0, 10) : "—"} mono />
              <KV label="Released Date" value={eng.released_date || "—"} mono />
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Design Lead Time</div>
                <div className="mt-1 font-mono text-3xl font-bold text-slate-900" data-testid="eng-lead-time">
                  {eng.design_lead_time_days != null ? `${eng.design_lead_time_days}` : "—"}
                  <span className="text-base text-slate-500 ml-1">days</span>
                </div>
              </div>
            </div>
          </Card>
          {eng.approved_by_name && (
            <Card className="border-slate-200 p-5">
              <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Approval</div>
              <KV label="Approved By" value={eng.approved_by_name} />
              <KV label="Approval Date" value={eng.approved_at ? new Date(eng.approved_at).toLocaleString() : "—"} />
              {eng.approval_remarks && <div className="mt-2 text-xs text-slate-600 italic">&quot;{eng.approval_remarks}&quot;</div>}
            </Card>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Revision History</div>
          <div className="text-xs text-slate-500">Every revision is preserved. Only one is Current at any time.</div>
        </div>
        <div className="p-5">
          {(d.revisions || []).length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">No revisions yet. Create the first one to get started.</div>
          ) : (
            <div className="space-y-2">
              {d.revisions.map((r) => (
                <div key={r.id} data-testid={`rev-${r.drawing_number}-${r.revision_number}`}
                     className={`p-3 border rounded ${r.status === "Current" || r.status === "Released" ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-bold text-slate-900">{r.drawing_number}</span>
                      <span className="font-mono text-sm font-semibold text-blue-900">{r.revision_number}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        r.status === "Released" ? "bg-emerald-100 text-emerald-800" :
                        r.status === "Current" ? "bg-blue-100 text-blue-800" :
                        "bg-slate-100 text-slate-600"}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">{r.created_at.slice(0, 10)} · {r.created_by_name}</div>
                  </div>
                  {r.drawing_description && (
                    <div className="mt-1 text-xs text-slate-600">{r.drawing_description}</div>
                  )}
                  {r.released_by_name && (
                    <div className="mt-1 text-[11px] text-emerald-700">
                      ✓ Released by {r.released_by_name} on {r.released_at?.slice(0, 10)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Audit Trail</div>
        <AuditTrail items={eng.audit_trail || []} />
      </Card>

      {/* New Revision Dialog */}
      <Dialog open={revDialog} onOpenChange={setRevDialog}>
        <DialogContent data-testid="new-rev-dialog">
          <DialogHeader>
            <DialogTitle>Create New Revision</DialogTitle>
            <DialogDescription>Any previous Current revision will be marked Superseded.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Drawing Number *</Label>
              <Input data-testid="rev-drawing-number" value={revForm.drawing_number} className="mt-1 font-mono"
                     onChange={(e) => setRevForm({ ...revForm, drawing_number: e.target.value })}
                     placeholder="e.g. GA-25101" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Revision Number *</Label>
              <Input data-testid="rev-revision-number" value={revForm.revision_number} className="mt-1 font-mono"
                     onChange={(e) => setRevForm({ ...revForm, revision_number: e.target.value.toUpperCase() })}
                     placeholder="R0 / R1 / R2..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Description (optional)</Label>
              <Input value={revForm.drawing_description}
                     onChange={(e) => setRevForm({ ...revForm, drawing_description: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Revision Remarks</Label>
              <Textarea rows={2} value={revForm.remarks}
                        onChange={(e) => setRevForm({ ...revForm, remarks: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevDialog(false)}>Cancel</Button>
            <Button data-testid="rev-submit" onClick={submitRevision} disabled={saving} className="bg-blue-900 hover:bg-blue-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve &amp; Release Engineering</DialogTitle>
            <DialogDescription>
              This will lock the current revision, record your approval, and make the cylinder available for Production.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Approval Remarks (optional)</Label>
            <Textarea rows={2} value={approveRemarks} className="mt-1"
                      onChange={(e) => setApproveRemarks(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancel</Button>
            <Button data-testid="approve-confirm" onClick={approve} disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve & Release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject / Request Revision Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>{d.can_approve ? "Reject / Request Revision" : "Request Revision"}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Reason *</Label>
            <Textarea data-testid="reject-reason" rows={3} value={reason} className="mt-1"
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="What needs to be revised or why is this being rejected?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            {d.can_approve && (
              <Button data-testid="reject-confirm" onClick={reject} disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject"}
              </Button>
            )}
            <Button data-testid="request-rev-confirm"
                    onClick={async () => {
                      if (!reason.trim()) { toast.error("Reason required"); return; }
                      setSaving(true);
                      try {
                        await api.post(`/engineering/${id}/request-revision`, { reason });
                        toast.success("Revision requested");
                        setRejectDialog(false); setReason("");
                        refresh();
                      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      finally { setSaving(false); }
                    }}
                    disabled={saving}
                    className="bg-rose-600 hover:bg-rose-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-slate-300">—</span>}
      </dd>
    </div>
  );
}
